/**
 * Contrato del Nodo — SSOT canónico.
 *
 * El nodo es la entidad organizacional del sistema (jerarquía fractal:
 * operador → representante → punto_venta). Tiene:
 *   - Columnas SQL propias: id, tipo, parent_id, nombre, codigo, activo.
 *   - Columna config_json: blob JSON con TODA la configuración de negocio.
 *
 * Este contrato describe los campos del config_json (más `nombre`, `codigo`,
 * `activo` que también edita el modal). El campo `tipo` define qué subset
 * del contrato aplica vía `aplicaSi`.
 *
 * NOTA crítica de polimorfismo:
 *   Cuando el modal abre un nodo, el Borrador recibe la copia COMPLETA
 *   (config + tipo). El `tipo` queda dentro de la copia de trabajo solo
 *   para que `aplicaSi(config)` pueda consultarlo. Al enviar al backend
 *   se separa: tipo va como columna, el resto va a config_json.
 *
 * Patrón flat con paths con puntos. Para sub-objetos como `fiscal.x`,
 * declaramos cada campo de fiscal por separado — no el objeto entero.
 * Esto permite validación granular y evita pisar sub-objetos completos
 * por descuido.
 */

const TIPO_NODO_OPCIONES = ['operador', 'representante', 'punto_venta'];
const MODALIDAD_PUNTO_VENTA_OPCIONES = ['fisico', 'online', 'ambulante', 'sistema'];
// Espejo del CHECK de `dispositivos.caja_subtipo` (schema.sql): toda frontera que
// reciba un subtipo lo valida contra esta lista antes de escribir.
const SUBTIPO_CAJA_OPCIONES = ['android', 'web'];

// ─── Adapters de protocolo del integrador — catálogo ÚNICO tipado ──────────
// SSOT compartido: el <select> del modal admin, la validación del backend y
// el despacho del runtime (partner-http-client) derivan TODOS de esta lista.
// Un protocolo fuera del catálogo no llega a firmar dinero: el transporte
// rechaza con error tipado antes de cualquier HTTP.
//
// `wireVersion` es la versión del contrato EXTERNO (wire) de cada adapter:
// se congela en cada acuerdo y en cada operación durable para auditoría y
// evolución del contrato. Distinta del adapter (`valor`), que despacha código.
const PROTOCOLO_INTEGRADOR_ESTANDAR_TIGRAO = 'tigrao';
const PROTOCOLO_INTEGRADOR_NATIVO_LOTO_SYSTEM = 'loto-system';
// Operaciones producidas por la wallet interna Ruleta; no es un transporte B2B.
const WIRE_VERSION_PROVIDER_WALLET = 'provider-wallet.v1';
const ADAPTERS_PROTOCOLO_INTEGRADOR = [
    {
        valor: PROTOCOLO_INTEGRADOR_ESTANDAR_TIGRAO,
        wireVersion: 'tigrao.1',
        etiqueta: 'Tigrao Wallet (estándar)',
        descripcion:
            'HMAC-SHA256 sobre la canónica JSON del payload completo — el del contrato público (docs/publico/API.md).',
    },
    {
        valor: PROTOCOLO_INTEGRADOR_NATIVO_LOTO_SYSTEM,
        wireVersion: 'loto-system.1',
        etiqueta: 'LOTO-SYSTEM (nativo)',
        descripcion: 'HMAC sobre el body JSON completo.',
    },
];
const PROTOCOLOS_INTEGRADOR = ADAPTERS_PROTOCOLO_INTEGRADOR.map(adapter => adapter.valor);

// Versión wire congelable por adapter. Fail-fast: un protocolo fuera del
// catálogo no puede congelar operaciones (espejo del transporte fail-closed).
function wireVersionDelProtocolo(protocol) {
    const adapter = ADAPTERS_PROTOCOLO_INTEGRADOR.find(a => a.valor === protocol);
    if (!adapter) throw new Error(`Protocolo fuera del catálogo de adapters: ${protocol}`);
    return adapter.wireVersion;
}

const esPuntoVenta = config => config.tipo === 'punto_venta';
const esIntegradorExterno = config => config.tipo === 'punto_venta' && config.is_external === true;
const tieneFiscal = config =>
    !!(config.fiscal && (config.fiscal.razon_social || config.fiscal.numero_documento || config.fiscal.regimen));
const tieneBancario = config =>
    !!(config.bancario && (config.bancario.banco || config.bancario.numero_cuenta || config.bancario.titular));

const CONTRATO_NODO = {
    nombreEntidad: 'nodo',

    campos: {
        // ─── Columnas SQL (no config_json) ──────────────────────────────────
        nombre: {
            tipo: 'string',
            default: '',
            maxLength: 100,
            descripcion: 'Nombre visible del nodo. Obligatorio.',
        },
        codigo: {
            tipo: 'string',
            default: null,
            maxLength: 64,
            descripcion: 'Código único opcional (ej. OP-LIMA-001). NULL = sin código.',
        },
        tipo: {
            tipo: 'enum',
            opciones: TIPO_NODO_OPCIONES,
            default: 'punto_venta',
            nullable: false,
            descripcion: 'Tipo del nodo en la jerarquía fractal. Inmutable post-creación.',
        },
        activo: {
            tipo: 'entero',
            default: 1,
            minInclusive: 0,
            maxInclusive: 1,
            nullable: false,
            descripcion: 'Soft-delete: 0 = inactivo, 1 = activo.',
        },

        // ─── Config raíz (cualquier tipo) ───────────────────────────────────
        contacto: {
            tipo: 'string',
            default: null,
            maxLength: 200,
            descripcion: 'Contacto libre (teléfono, email).',
        },
        direccion: {
            tipo: 'string',
            default: null,
            maxLength: 300,
            descripcion: 'Dirección física.',
        },
        idioma: {
            tipo: 'string',
            default: null,
            maxLength: 16,
            descripcion: 'Idioma override del nodo. BCP 47 admite extended tags (ej. pt-BR-x-formal).',
        },
        moneda: {
            tipo: 'string',
            default: null,
            maxLength: 16,
            descripcion: 'Moneda override del nodo (null = heredar del padre).',
        },
        pais: {
            tipo: 'string',
            default: null,
            maxLength: 16,
            descripcion: 'País del nodo. Define presets fiscales/bancarios.',
        },

        // ─── Modalidad (solo punto_venta) ───────────────────────────────────
        modalidad: {
            tipo: 'enum',
            opciones: MODALIDAD_PUNTO_VENTA_OPCIONES,
            default: 'fisico',
            aplicaSi: esPuntoVenta,
            descripcion: 'Modalidad operativa del punto de venta.',
        },

        // ─── WhatsApp (cualquier tipo) ──────────────────────────────────────
        'whatsapp.habilitado': {
            tipo: 'booleano',
            default: true,
            nullable: false,
            descripcion: 'Si el canal WhatsApp está habilitado para este nodo.',
        },
        'whatsapp.telefono': {
            tipo: 'string',
            default: '',
            maxLength: 32,
            descripcion: 'Teléfono WhatsApp del nodo (solo dígitos).',
        },

        // ─── Fiscal (cualquier tipo, opcional) ──────────────────────────────
        'fiscal.razon_social': {
            tipo: 'string',
            default: null,
            maxLength: 200,
            descripcion: 'Razón social legal del nodo.',
        },
        'fiscal.tipo_documento': {
            tipo: 'string',
            default: null,
            maxLength: 16,
            descripcion: 'Tipo de documento fiscal (RUC, CNPJ, etc).',
        },
        'fiscal.numero_documento': {
            tipo: 'string',
            default: null,
            maxLength: 32,
            descripcion: 'Número del documento fiscal.',
        },
        'fiscal.direccion_fiscal': {
            tipo: 'string',
            default: null,
            maxLength: 300,
            descripcion: 'Dirección registrada en el documento fiscal.',
        },
        'fiscal.regimen': {
            tipo: 'string',
            default: null,
            maxLength: 64,
            descripcion: 'Régimen tributario.',
        },

        // ─── Bancario (cualquier tipo, opcional) ────────────────────────────
        'bancario.banco': { tipo: 'string', default: null, maxLength: 100, descripcion: 'Nombre del banco.' },
        'bancario.tipo_cuenta': {
            tipo: 'string',
            default: null,
            maxLength: 32,
            descripcion: 'Tipo de cuenta (ahorro, corriente).',
        },
        'bancario.numero_cuenta': {
            tipo: 'string',
            default: null,
            maxLength: 64,
            descripcion: 'Número de cuenta.',
        },
        'bancario.cuenta_interbancaria': {
            tipo: 'string',
            default: null,
            maxLength: 64,
            descripcion: 'Cuenta interbancaria (CCI, IBAN, etc).',
        },
        'bancario.titular': {
            tipo: 'string',
            default: null,
            maxLength: 200,
            descripcion: 'Titular de la cuenta bancaria.',
        },

        // ─── Autorización legal (solo punto_venta) ──────────────────────────
        'autorizacion_data.codigo': {
            tipo: 'string',
            default: null,
            maxLength: 64,
            aplicaSi: esPuntoVenta,
            descripcion: 'Código de autorización del PV.',
        },
        'autorizacion_data.estado': {
            tipo: 'string',
            default: null,
            maxLength: 32,
            aplicaSi: esPuntoVenta,
            descripcion: 'Estado de la autorización (vigente, vencida, etc).',
        },
        'autorizacion_data.fecha_emision': {
            tipo: 'string',
            default: null,
            maxLength: 32,
            aplicaSi: esPuntoVenta,
            descripcion: 'Fecha emisión autorización (ISO).',
        },
        'autorizacion_data.fecha_vencimiento': {
            tipo: 'string',
            default: null,
            maxLength: 32,
            aplicaSi: esPuntoVenta,
            descripcion: 'Fecha vencimiento autorización (ISO).',
        },

        // ─── Ubicación geográfica (solo punto_venta) ────────────────────────
        'ubicacion.region': {
            tipo: 'string',
            default: null,
            maxLength: 100,
            aplicaSi: esPuntoVenta,
            descripcion: 'Región/provincia del PV.',
        },
        'ubicacion.latitud': {
            tipo: 'numero',
            default: null,
            minInclusive: -90,
            maxInclusive: 90,
            aplicaSi: esPuntoVenta,
            descripcion: 'Latitud GPS del PV.',
        },
        'ubicacion.longitud': {
            tipo: 'numero',
            default: null,
            minInclusive: -180,
            maxInclusive: 180,
            aplicaSi: esPuntoVenta,
            descripcion: 'Longitud GPS del PV.',
        },
        'ubicacion.horario': {
            tipo: 'string',
            default: null,
            maxLength: 200,
            aplicaSi: esPuntoVenta,
            descripcion: 'Horario de atención (libre).',
        },
        'ubicacion.dias': {
            tipo: 'string',
            default: null,
            maxLength: 100,
            aplicaSi: esPuntoVenta,
            descripcion: 'Días de atención (libre).',
        },

        // ─── Integrador externo (solo punto_venta + is_external) ────────────
        is_external: {
            tipo: 'booleano',
            default: false,
            nullable: false,
            aplicaSi: esPuntoVenta,
            descripcion: 'Marca el PV como integrador externo (gateway de partner).',
        },
        partner_code: {
            tipo: 'string',
            default: null,
            maxLength: 64,
            aplicaSi: esIntegradorExterno,
            descripcion: 'Código del partner externo.',
        },
        protocol: {
            tipo: 'enum',
            opciones: PROTOCOLOS_INTEGRADOR,
            // El estándar es el protocolo del contrato público — lo único que
            // se ofrece a integradores nuevos; el nativo queda para internos.
            default: PROTOCOLO_INTEGRADOR_ESTANDAR_TIGRAO,
            nullable: false,
            aplicaSi: esIntegradorExterno,
            descripcion: 'Protocolo del integrador.',
        },
        b2b_frontend_url: {
            tipo: 'string',
            default: null,
            maxLength: 500,
            aplicaSi: esIntegradorExterno,
            descripcion: 'URL de nuestro Player B2B para launches LOBBY/GAME.',
        },
        url_balance: {
            tipo: 'string',
            default: null,
            maxLength: 500,
            aplicaSi: esIntegradorExterno,
            descripcion: 'URL endpoint balance del partner.',
        },
        url_betwin: {
            tipo: 'string',
            default: null,
            maxLength: 500,
            aplicaSi: esIntegradorExterno,
            descripcion: 'URL endpoint bet/win del partner.',
        },
        api_key: {
            tipo: 'string',
            default: null,
            maxLength: 256,
            aplicaSi: esIntegradorExterno,
            descripcion: 'API key del partner (no se devuelve completa al admin).',
        },
        api_secret: {
            tipo: 'string',
            default: null,
            maxLength: 256,
            aplicaSi: esIntegradorExterno,
            descripcion: 'API secret del partner (mascarado al admin).',
        },
        operator_id: {
            tipo: 'string',
            default: null,
            maxLength: 64,
            aplicaSi: esIntegradorExterno,
            descripcion: 'Operator ID en el sistema del partner.',
        },
        timeout_ms: {
            tipo: 'entero',
            default: null,
            minInclusive: 1000,
            maxInclusive: 60000,
            aplicaSi: esIntegradorExterno,
            descripcion: 'Timeout HTTP al partner (ms).',
        },

        // ─── Wallet, pasarela, juegos, ticketLegalPOS, brandingTicketPOS,
        //     series_tickets_por_canal, branding ─────────────────────────────
        // Estos sub-objetos son colecciones dinámicas (cada provider de
        // pasarela tiene su sub-shape, cada game type tiene sus restricciones,
        // etc). NO los desglosamos campo a campo — se preservan como objetos
        // opacos: el modal los arma como bloque entero, el contrato solo
        // valida que SEAN objetos. El backend hace deepMergeConfig al
        // persistir, así que mandar el bloque parcial preserva campos no
        // enviados sin necesidad de comparaciones canónicas en el frontend.
        wallet: {
            tipo: 'objeto',
            default: null,
            maxKeys: 32,
            descripcion: 'Config de wallet (denominaciones, límites).',
        },
        pasarela: {
            tipo: 'objeto',
            default: null,
            maxKeys: 32,
            descripcion: 'Config de proveedores de pasarela de pagos.',
        },
        juegos: {
            tipo: 'objeto',
            default: null,
            maxKeys: 64,
            descripcion: 'Restricciones por game type.',
        },
        ticketLegalPOS: {
            tipo: 'objeto',
            default: null,
            maxKeys: 32,
            descripcion: 'Texto legal del ticket impreso.',
        },
        brandingTicketPOS: {
            tipo: 'objeto',
            default: null,
            maxKeys: 32,
            descripcion: 'Branding del ticket impreso.',
        },
        series_tickets_por_canal: {
            tipo: 'objeto',
            default: null,
            maxKeys: 16,
            descripcion: 'Override de series de tickets por canal (solo operadores).',
        },
        branding: {
            tipo: 'objeto',
            default: null,
            maxKeys: 32,
            descripcion: 'Branding del nodo (logos, colores).',
        },
    },
};

/**
 * ¿Este nodo es un Promotor Digital? DETECTOR CANÓNICO, único para backend y fronts.
 *
 * Un Promotor Digital es una PERSONA que comparte su enlace de afiliación, no un local: no vende
 * tickets, no tiene cajas ni pantallas, y nada cuelga de él. Su único dispositivo es el enlace web
 * a su panel. El perfil del admin ya lo declaraba (`arbolPlano`, `contratoTabs: ['comisiones']`),
 * Es el detector que deben usar los CONSUMIDORES NUEVOS y los que se vayan tocando. Todavía NO es el
 * único: quedan consultas SQL con `LIKE '%"es_promotor_digital":true%'` (contratos, relatorio, wallet,
 * jobs) y el perfil del front-admin con su propia condición. Unificarlas es una limpieza aparte, con
 * pruebas de equivalencia — meterla en un hotfix agregaría riesgo sin cerrar ninguna frontera.
 *
 * Acepta el nodo con `config` ya parseado o con `config_json` crudo: los callers del backend leen
 * de la DB (JSON string) y los del front reciben el objeto. Un flag en `false` NO es promotor —
 * es el estado que queda al convertir uno en PV normal, y debe distinguirse de la ausencia del flag.
 *
 * @param {{config?: Object, config_json?: string}|null|undefined} nodo
 * @returns {boolean}
 */
function esPromotorDigital(nodo) {
    if (!nodo) return false;
    let config = nodo.config;
    if (!config && typeof nodo.config_json === 'string') {
        try {
            config = JSON.parse(nodo.config_json);
        } catch (_e) {
            return false; // config ilegible: no se inventa un perfil
        }
    }
    return config?.es_promotor_digital === true;
}

/** Error tipado de la regla estructural del promotor — común a todas las fronteras. */
const CODIGO_NADA_CUELGA_DE_PROMOTOR = 'NADA_CUELGA_DE_UN_PROMOTOR_DIGITAL';

/**
 * Un Promotor Digital NO tiene nada debajo: ni nodos hijos, ni cajas, ni pantallas.
 *
 * Es UNA persona que comparte su enlace, no una estructura. Su único dispositivo es el enlace web a
 * su panel — eso es su forma de existir, no un "hijo". La regla se aplica en las fronteras que crean
 * o mueven cosas hacia un nodo, porque nada la impedía: en producción aparecieron 2 cajas Android y
 * 2 TVs colgadas de 3 promotores distintos (ninguna vendió jamás; se limpiaron antes de este cambio).
 *
 * @param {{config?: Object, config_json?: string, nombre?: string}|null} padreDestino
 * @param {string} queSeIntentaColgar - texto para el mensaje ('esta caja', 'este nodo'…)
 * @returns {string|null} motivo del rechazo, o null si el destino admite hijos
 */
function motivoSiNadaPuedeColgarDelPromotor(padreDestino, queSeIntentaColgar) {
    if (!esPromotorDigital(padreDestino)) return null;
    const nombre = padreDestino?.nombre ? `"${padreDestino.nombre}"` : 'ese nodo';
    return (
        `No se puede agregar ${queSeIntentaColgar} a ${nombre}: es un Promotor Digital, y de un promotor ` +
        `no cuelga nada (ni cajas, ni pantallas, ni nodos). Su único acceso es el enlace a su panel. ` +
        `Si necesita operar un local, primero convertilo en Punto de Venta.`
    );
}

/**
 * La ÚNICA excepción a "de un promotor no cuelga nada": su caja-enlace.
 *
 * No es un aparato: es la forma en que el promotor existe — el link que comparte para captar jugadores
 * y entrar a su panel. Su forma canónica está fijada por los 81 dispositivos de promotores en producción,
 * todos idénticos: `caja` + subtipo `web` + `operaciones: ['enlace']`.
 *
 * El predicado es ESTRICTO a propósito. "Ya está colgado del promotor" NO alcanza como excepción: eso
 * autorizaría retroactivamente cualquier aparato que se haya colado (fue exactamente el caso de las 2
 * cajas Android y 2 TVs que hubo que limpiar). Solo pasa lo que coincide con la forma canónica.
 *
 * Tolera el config como objeto o como TEXT crudo: los callers del backend leen de la DB, el modal manda
 * el objeto ya armado.
 *
 * @param {{tipo?: string, caja_subtipo?: string, config?: Object|string}|null} dispositivo
 * @returns {boolean}
 */
function esLaCajaEnlaceDelPromotor(dispositivo) {
    if (!dispositivo) return false;
    if (dispositivo.tipo !== 'caja') return false;
    // FAIL-CLOSED: se exige el subtipo 'web' EXPLÍCITO. Tolerar `undefined` abría el bypass — una caja
    // android pendiente, autorizada hacia un promotor con `config.operaciones:['enlace']`, pasaba el
    // predicado porque el getter no exponía el subtipo real. La excepción es una forma concreta, no
    // "lo que el payload declare"; ante la duda, no es el enlace.
    if (dispositivo.caja_subtipo !== 'web') return false;

    let config = dispositivo.config;
    if (typeof config === 'string') {
        try {
            config = JSON.parse(config);
        } catch (_e) {
            return false; // config ilegible: no se le concede la excepción
        }
    }
    const operaciones = config?.operaciones;
    // EXACTAMENTE ['enlace']: un aparato que además declare wallet o tickets no es el enlace, es una caja
    // operativa — y esa no puede colgar de un promotor.
    return Array.isArray(operaciones) && operaciones.length === 1 && operaciones[0] === 'enlace';
}

/**
 * ¿Puede este DISPOSITIVO colgar del nodo destino? Regla compartida por todas las fronteras que crean,
 * vinculan o mueven aparatos (alta, generar-apk, PUT, y los MCP), para que no puedan divergir.
 *
 * @param {Object|null} nodoDestino
 * @param {Object|null} dispositivo - el que se crearía/movería (shape de DB o payload de alta)
 * @returns {string|null} motivo del rechazo, o null si está permitido
 */
function motivoSiElDispositivoNoPuedeColgarDelPromotor(nodoDestino, dispositivo, opciones = {}) {
    if (!esPromotorDigital(nodoDestino)) return null;
    const nombre = nodoDestino?.nombre ? `"${nodoDestino.nombre}"` : 'ese nodo';

    if (esLaCajaEnlaceDelPromotor(dispositivo)) {
        // CARDINALIDAD: "su ÚNICO acceso" es literal. Validar solo la forma dejaba agregar un segundo
        // enlace — dos credenciales vivas para el mismo promotor, y `getCajaWebDeNodo` elegiría la más
        // reciente, así que la otra quedaría activa sin que nadie la vea. `enlacesQueYaTiene` lo cuenta
        // SIN contar el dispositivo que se está editando (mover o actualizar su propio enlace no es
        // agregar uno).
        //
        // FAIL-CLOSED, y es el punto: mientras el dato fue OPCIONAL, omitirlo AUTORIZABA — y
        // `mover_dispositivo` del MCP lo omitía, así que movía un segundo enlace hacia un promotor que
        // ya tenía el suyo. Un caller no puede saltearse una invariante por descuido: si no informa la
        // cardinalidad, no se le concede la excepción.
        // Sin `Number()`: la coerción era el agujero. `Number(null)`, `Number('')`, `Number(false)` y
        // `Number([])` valen 0, así que cuatro formas de NO informar la cardinalidad se leían como
        // "no tiene ninguno" y autorizaban. Solo un entero explícito ≥ 0 cuenta como haber informado.
        const yaTiene = opciones.enlacesQueYaTiene;
        if (!Number.isInteger(yaTiene) || yaTiene < 0) {
            return (
                `No se puede autorizar la caja de enlace de ${nombre}: quien intenta la operación no ` +
                `informó cuántos enlaces tiene ya el promotor, y sin ese dato no se puede garantizar que ` +
                `quede con UN solo acceso. Es un error de programación en el llamador, no del dato.`
            );
        }
        if (yaTiene > 0) {
            return (
                `No se puede agregar otra caja de enlace a ${nombre}: ya tiene la suya. Un Promotor Digital ` +
                `tiene UN solo acceso a su panel — dos credenciales vivas es justo lo que hay que evitar. ` +
                `Para renovar el acceso, regenerá el enlace existente en vez de crear otro.`
            );
        }
        return null; // su enlace: es cómo existe
    }

    return (
        `No se puede asignar este dispositivo a ${nombre}: es un Promotor Digital. Su único dispositivo ` +
        `es la caja de enlace a su panel (web, operaciones: ['enlace']) — no admite cajas operativas, ` +
        `TVs ni terminales VLT. Si necesita operar un local, primero convertilo en Punto de Venta.`
    );
}

/**
 * ¿Este error viene del índice que garantiza UN solo enlace por nodo (`v171`)?
 *
 * Los guards cuentan y DESPUÉS escriben, así que quien PIERDE una carrera concurrente no pasa por el
 * guard: su rechazo llega desde la DB como `SqliteError` crudo y, sin traducir, sale como 500. Para
 * quien llama, perder la carrera y ser rechazado de entrada son el MISMO caso y merecen la misma
 * respuesta.
 *
 * Vive acá, junto a la regla, y no en cada writer: la primera versión de esto quedó en un solo `catch`
 * de `generar-apk` y las otras tres fronteras siguieron devolviendo 500. Un traductor por call site es
 * la misma trampa que un parámetro opcional — se olvida en silencio.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function esViolacionDeEnlaceUnicoPorNodo(error) {
    const mensaje = (error && (error.message || String(error))) || '';
    return (
        /idx_dispositivos_un_enlace_por_nodo/.test(mensaje) ||
        /UNIQUE constraint failed:\s*dispositivos\.nodo_id/.test(mensaje)
    );
}

/** Mensaje único para el perdedor de la carrera. Mismo `code` que el rechazo preventivo. */
const MOTIVO_ENLACE_UNICO_POR_CARRERA =
    'No se puede agregar otra caja de enlace a este nodo: ya tiene la suya. Si acabás de crearla, ' +
    'recargá la pantalla para verla.';

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        CONTRATO_NODO,
        SUBTIPO_CAJA_OPCIONES,
        ADAPTERS_PROTOCOLO_INTEGRADOR,
        WIRE_VERSION_PROVIDER_WALLET,
        wireVersionDelProtocolo,
        PROTOCOLO_INTEGRADOR_ESTANDAR_TIGRAO,
        PROTOCOLO_INTEGRADOR_NATIVO_LOTO_SYSTEM,
        esPromotorDigital,
        motivoSiNadaPuedeColgarDelPromotor,
        CODIGO_NADA_CUELGA_DE_PROMOTOR,
        esLaCajaEnlaceDelPromotor,
        motivoSiElDispositivoNoPuedeColgarDelPromotor,
        esViolacionDeEnlaceUnicoPorNodo,
        MOTIVO_ENLACE_UNICO_POR_CARRERA,
    };
}

export {
    CONTRATO_NODO,
    SUBTIPO_CAJA_OPCIONES,
    ADAPTERS_PROTOCOLO_INTEGRADOR,
    WIRE_VERSION_PROVIDER_WALLET,
    wireVersionDelProtocolo,
    PROTOCOLO_INTEGRADOR_ESTANDAR_TIGRAO,
    PROTOCOLO_INTEGRADOR_NATIVO_LOTO_SYSTEM,
    esPromotorDigital,
    motivoSiNadaPuedeColgarDelPromotor,
    CODIGO_NADA_CUELGA_DE_PROMOTOR,
    esLaCajaEnlaceDelPromotor,
    motivoSiElDispositivoNoPuedeColgarDelPromotor,
    esViolacionDeEnlaceUnicoPorNodo,
    MOTIVO_ENLACE_UNICO_POR_CARRERA,
};
